import {getBcp47LanguageTag} from '@aboutyou/config';
import {useDateFormat, useLocale} from './I18nContext';
import {DateFormatLong} from './getLongDateFormatForLocale';
import {DateFormatShort} from './getShortDateFormatForLocale';

export type {DateFormatLong, DateFormatShort};

export type DateFormat = DateFormatLong | DateFormatShort;

const yearRegex = /yyyy/;
const monthRegex = /mm/;
const dayRegex = /dd/;

/**
 * @internal Ideally, there should be no need for this function to be public for other packages,
 * instead, one of the specific formatting functions below should be used for various use cases.
 * If the need arises for a different formatting function, create one and make it public.
 */
export const getFormattedDate = (date: Date, format: DateFormat): string => {
  const day = date.getDate();
  const dayString = day.toString().padStart(2, '0');

  const month = date.getMonth() + 1;
  const monthString = month.toString().padStart(2, '0');

  const yearString = date.getFullYear().toString();

  let formattedDate = format.replace(dayRegex, dayString);
  formattedDate = formattedDate.replace(monthRegex, monthString);
  formattedDate = formattedDate.replace(yearRegex, yearString);

  return formattedDate;
};

export const isWithinXDays = (targetDate: Date, counter: number) => {
  if (counter < 1) {
    return false;
  }
  const futureDateTime = counter * 24 * 60 * 60 * 1000;
  const today = new Date();
  const futureDay = new Date(Date.now() + futureDateTime);

  futureDay.setHours(23, 59, 59, 999);

  return targetDate > today && targetDate < futureDay;
};

export const getDayDifferencesBetweenDates = (start: Date, end: Date) => {
  const oneDay = 24 * 60 * 60 * 1000;

  end.setHours(23, 59, 59, 999);

  const diffDays = Math.floor((end.getTime() - start.getTime()) / oneDay);
  return diffDays;
};

export const useFormatDateWithShortWeekName = () => {
  const locale = useLocale();

  const options = {
    weekday: 'short',
    day: 'numeric',
    month: 'numeric',
    // this is required in order to make sure that on browsers which does not use this as default (e.g. Safari)
    // the date is displayed with latin characters for locales which use a different alphabet as well
    numberingSystem: 'latn',
  } as Intl.DateTimeFormatOptions;

  return (date: Date, shouldIncludeYear = false) => {
    if (shouldIncludeYear) {
      options.year = 'numeric';
    }

    return date.toLocaleDateString(getBcp47LanguageTag(locale), options);
  };
};

export const useFormatDateWithDayAndMonth = () => {
  const locale = useLocale();

  return (date: Date) => {
    const formatted = date.toLocaleDateString(getBcp47LanguageTag(locale), {
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    // Transform the format to "5. September 19.00"
    // Replace the space between day and month with ". "
    // Replace ":" in time with "."
    return formatted.replace(/(\d+)\s+/, '$1. ');
  };
};

export const useFormatDateWithYearOnlyIfNeeded = () => {
  const dateFormat = useDateFormat();

  return (date: Date) =>
    getFormattedDate(
      date,
      date.getFullYear() === new Date().getFullYear()
        ? dateFormat.short
        : dateFormat.long,
    );
};

export const useFormatDateWithYear = () => {
  const {long} = useDateFormat();

  return (date: Date) => getFormattedDate(date, long);
};

/**
 * Formats a date to show "HH:mm"
 */
export const useGetHourMinuteString = () => {
  const locale = useLocale();

  return (date: Date) =>
    date.toLocaleTimeString(getBcp47LanguageTag(locale), {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
};

export const isPastDate = (date: Date) => {
  const now = new Date();
  return date < now;
};

export const isToday = (date: Date) => {
  const today = new Date();
  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  );
};
